import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import { ContentCopy as CopyIcon, Key as KeyIcon, Block as RevokeIcon } from '@mui/icons-material';
import api from '../../config/axiosConfig';
import { claudeCodeCommand, tokenStatus } from '../../utils/apiTokens';

const formatDate = (value) => (value ? new Date(value).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const ApiTokensSection = () => {
  const [tokens, setTokens] = useState([]);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('Claude Code');
  const [validityDays, setValidityDays] = useState(90);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await api.get('/users/me/api-tokens');
      setTokens(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (e) {
      setError('No se pudieron cargar tus tokens');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const response = await api.post('/users/me/api-tokens', { name, validityDays });
      setCreated(response.data);
      setCreateOpen(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo crear el token');
    }
  };

  const handleRevoke = async (token) => {
    if (!window.confirm(`¿Revocar el token "${token.name}"? Claude dejará de tener acceso con él.`)) return;
    try {
      await api.delete(`/users/me/api-tokens/${token.id}`);
      load();
    } catch (e) {
      setError('No se pudo revocar el token');
    }
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch (e) {
      setCopied('');
    }
  };

  return (
    <Paper sx={{ p: 4, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <KeyIcon sx={{ color: '#1976d2' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Tokens para Claude</Typography>
        </Box>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>Generar token</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Permiten que Claude consulte CONEM con tu usuario (solo lectura): OTs por cerrar hoy, atrasadas,
        campos faltantes para RIC/PIPC, UIPC, visitas y direcciones. Revoca un token si ya no lo usas.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {tokens.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Aún no tienes tokens.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Token</TableCell>
              <TableCell>Creado</TableCell>
              <TableCell>Último uso</TableCell>
              <TableCell>Vence</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.map((t) => {
              const status = tokenStatus(t);
              return (
                <TableRow key={t.id}>
                  <TableCell>{t.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{t.prefix}…</TableCell>
                  <TableCell>{formatDate(t.createdAt)}</TableCell>
                  <TableCell>{formatDate(t.lastUsedAt)}</TableCell>
                  <TableCell>{formatDate(t.expiresAt)}</TableCell>
                  <TableCell><Chip size="small" label={status.label} color={status.color} /></TableCell>
                  <TableCell align="right">
                    {status.label === 'Activo' && (
                      <Tooltip title="Revocar">
                        <IconButton size="small" onClick={() => handleRevoke(t)}><RevokeIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generar token para Claude</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Nombre (ej. Claude Code laptop)" value={name}
            onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 100 }} />
          <TextField select fullWidth margin="normal" label="Vigencia" value={validityDays}
            onChange={(e) => setValidityDays(Number(e.target.value))}>
            <MenuItem value={30}>30 días</MenuItem>
            <MenuItem value={90}>90 días</MenuItem>
            <MenuItem value={180}>180 días</MenuItem>
            <MenuItem value={365}>1 año</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!name.trim()}>Generar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!created} onClose={() => { setCreated(null); setCopied(''); }} maxWidth="md" fullWidth>
        <DialogTitle>Token generado</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Cópialo ahora: por seguridad no se vuelve a mostrar.</Alert>
          <Typography variant="subtitle2">Token</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{created?.token}</Typography>
            <IconButton onClick={() => copy(created?.token, 'token')}><CopyIcon /></IconButton>
          </Box>
          <Typography variant="subtitle2">Comando para Claude Code</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', flex: 1, bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
              {created ? claudeCodeCommand(created.token) : ''}
            </Typography>
            <IconButton onClick={() => copy(claudeCodeCommand(created?.token), 'command')}><CopyIcon /></IconButton>
          </Box>
          {copied && <Typography variant="caption" color="success.main">{copied === 'token' ? 'Token copiado' : 'Comando copiado'}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { setCreated(null); setCopied(''); }}>Listo, ya lo copié</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ApiTokensSection;
